import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { SystemLog } from '@wf-coe/types';
import { useApiClient } from '../lib/use-api-client.js';

/**
 * Filter contract passed straight through to
 * `GET /v1/admin/system-logs/{licenseId}` → engine
 * `GET /engine/v1/system-logs`. All optional; engine defaults to
 * "last 24 hours" when `from`/`to` are absent.
 */
export interface SystemLogsQuery {
  /** ISO 8601, e.g. `2026-05-11T00:00:00Z`. */
  from?: string;
  to?: string;
  /** `Error` / `Warning` / `Info`. */
  type?: string;
  /** Pipeline process name (free-form, matched exactly on the engine side). */
  process?: string;
  /** Cap newest-first row count (1–1000). Default in the UI is 30;
   *  the engine returns rows in descending log-time order so this
   *  also avoids the SPA having to load + re-sort the whole window. */
  limit?: number;
}

interface SystemLogsResponseDto {
  data: SystemLog[];
}

/**
 * Hook options. `refetchIntervalMs` powers the Grafana-style
 * auto-refresh dropdown in `SystemLogs.tsx` — passes straight through
 * to TanStack Query's `refetchInterval`. Pass `false` (the default)
 * to disable.
 *
 * TanStack pauses auto-refetch when the tab is hidden (default
 * `refetchIntervalInBackground: false`), so leaving "Auto: 5s" on
 * doesn't keep hammering the engine when the operator switches away.
 */
export interface UseSystemLogsQueryOptions {
  refetchIntervalMs?: number | false;
}

export function useSystemLogsQuery(
  licenseId: string | undefined,
  filter: SystemLogsQuery,
  options: UseSystemLogsQueryOptions = {},
): UseQueryResult<SystemLog[], Error> {
  const api = useApiClient();
  return useQuery({
    queryKey: ['system-logs', licenseId, filter] as const,
    enabled: Boolean(api && licenseId),
    staleTime: 15_000,
    refetchInterval: options.refetchIntervalMs ?? false,
    queryFn: async () => {
      if (!api) throw new Error('useSystemLogsQuery: api client unavailable');
      if (!licenseId) throw new Error('useSystemLogsQuery: licenseId required');
      const params = new URLSearchParams();
      if (filter.from) params.set('from', filter.from);
      if (filter.to) params.set('to', filter.to);
      if (filter.type) params.set('type', filter.type);
      if (filter.process) params.set('process', filter.process);
      if (filter.limit !== undefined) params.set('limit', String(filter.limit));
      const qs = params.size ? `?${params.toString()}` : '';
      const res = await api.get<SystemLogsResponseDto>(`/v1/admin/system-logs/${licenseId}${qs}`);
      return res.data;
    },
  });
}
